import { useEffect, useRef, useState } from 'react'

/** Færdigt billede gemmes som kvadrat i denne størrelse (eller mindre, hvis udsnittet er mindre). */
const OUTPUT_SIZE = 512
const MAX_ZOOM = 4
const JPEG_QUALITY = 0.85

interface Point {
  x: number
  y: number
}

/**
 * Beskæringsværktøj til profilbilleder.
 *
 * Brugeren vælger selv udsnittet: træk for at flytte, zoom med musehjul, knib på
 * touch eller skyderen. Cirklen viser hvad der ses i den runde avatar - selve
 * billedet gemmes som kvadrat, så det også kan bruges firkantet senere.
 *
 * Billedet vises med et <img>-element, og det samme element bruges som kilde ved
 * eksport. Dermed er EXIF-rotation fra mobilkameraer ens i visning og resultat.
 */
export function ImageCropper({
  file,
  busy = false,
  onCancel,
  onCrop
}: {
  file: Blob
  busy?: boolean
  onCancel: () => void
  onCrop: (blob: Blob) => void | Promise<void>
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null)
  const [viewport, setViewport] = useState(0)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 })
  const [error, setError] = useState<string | null>(null)

  const viewportRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  /** Aktive fingre/muspegere, så både træk og knib kan håndteres. */
  const pointers = useRef(new Map<number, Point>())
  const pinch = useRef<{ distance: number; zoom: number } | null>(null)

  // Object-URL til det valgte billede.
  useEffect(() => {
    const url = URL.createObjectURL(file)
    setSrc(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  // Viewportens størrelse afhænger af skærmen (mobil vs. desktop).
  useEffect(() => {
    const element = viewportRef.current
    if (!element) return

    const measure = () => setViewport(element.getBoundingClientRect().width)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [src])

  // Mindste zoom er det der lige præcis dækker viewporten.
  const cover = natural && viewport ? Math.max(viewport / natural.width, viewport / natural.height) : 1
  const scale = cover * zoom

  /** Holder billedet inden for viewporten, så der aldrig opstår tomme kanter. */
  function clampOffset(next: Point, atScale: number): Point {
    if (!natural) return next
    const width = natural.width * atScale
    const height = natural.height * atScale
    return {
      x: Math.min(0, Math.max(viewport - width, next.x)),
      y: Math.min(0, Math.max(viewport - height, next.y))
    }
  }

  /** Zoomer omkring et punkt i viewporten (musemarkør, knibecenter eller midten). */
  function applyZoom(nextZoom: number, anchor?: Point) {
    if (!natural || !viewport) return

    const limited = Math.min(MAX_ZOOM, Math.max(1, nextZoom))
    const point = anchor ?? { x: viewport / 2, y: viewport / 2 }
    const nextScale = cover * limited

    // Behold det punkt i billedet der ligger under ankeret.
    const imageX = (point.x - offset.x) / scale
    const imageY = (point.y - offset.y) / scale

    setOffset(clampOffset({ x: point.x - imageX * nextScale, y: point.y - imageY * nextScale }, nextScale))
    setZoom(limited)
  }

  // Musehjul: registreres som ikke-passiv, så siden bag ikke scroller med.
  useEffect(() => {
    const element = viewportRef.current
    if (!element) return

    function onWheel(event: WheelEvent) {
      event.preventDefault()
      const rect = element!.getBoundingClientRect()
      applyZoom(zoom * (event.deltaY < 0 ? 1.12 : 1 / 1.12), {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      })
    }

    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  })

  // Escape lukker.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  function onImageLoad(event: React.SyntheticEvent<HTMLImageElement>) {
    const image = event.currentTarget
    const size = { width: image.naturalWidth, height: image.naturalHeight }
    setNatural(size)
    setZoom(1)

    // Start centreret.
    const box = viewportRef.current?.getBoundingClientRect().width ?? viewport
    const start = Math.max(box / size.width, box / size.height)
    setOffset({ x: (box - size.width * start) / 2, y: (box - size.height * start) / 2 })
  }

  function pointerPositions() {
    return [...pointers.current.values()]
  }

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (pointers.current.size === 2) {
      const [a, b] = pointerPositions()
      pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), zoom }
    }
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const previous = pointers.current.get(event.pointerId)
    if (!previous) return

    const current = { x: event.clientX, y: event.clientY }
    pointers.current.set(event.pointerId, current)

    if (pointers.current.size === 1) {
      setOffset((existing) =>
        clampOffset({ x: existing.x + (current.x - previous.x), y: existing.y + (current.y - previous.y) }, scale)
      )
      return
    }

    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = pointerPositions()
      const distance = Math.hypot(a.x - b.x, a.y - b.y)
      const rect = viewportRef.current?.getBoundingClientRect()
      if (!rect || distance === 0) return

      applyZoom(pinch.current.zoom * (distance / pinch.current.distance), {
        x: (a.x + b.x) / 2 - rect.left,
        y: (a.y + b.y) / 2 - rect.top
      })
    }
  }

  function onPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId)
    if (pointers.current.size < 2) {
      pinch.current = null
    }
  }

  async function save() {
    const image = imageRef.current
    if (!image || !natural || !viewport) return

    setError(null)

    // Udsnittet omregnes fra viewport-koordinater til billedets egne pixels.
    const sourceSize = viewport / scale
    const sourceX = -offset.x / scale
    const sourceY = -offset.y / scale
    const target = Math.max(64, Math.min(OUTPUT_SIZE, Math.round(sourceSize)))

    const canvas = document.createElement('canvas')
    canvas.width = target
    canvas.height = target

    const context = canvas.getContext('2d')
    if (!context) {
      setError('Browseren kunne ikke behandle billedet.')
      return
    }

    // Hvid bund, så gennemsigtige PNG-billeder ikke bliver sorte som JPEG.
    context.fillStyle = '#FFFFFF'
    context.fillRect(0, 0, target, target)
    context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, target, target)

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
    if (!blob) {
      setError('Billedet kunne ikke konverteres.')
      return
    }

    await onCrop(blob)
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Beskær profilbillede">
      <div className="modal__card">
        <h2 className="card__title" style={{ marginBottom: 4 }}>Beskær profilbillede</h2>
        <p className="tiny muted" style={{ marginTop: 0 }}>
          Træk billedet på plads, og zoom til det udsnit du vil bruge. Cirklen viser, hvordan billedet ses som avatar.
        </p>

        <div
          className="cropper"
          ref={viewportRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {src && (
            <img
              ref={imageRef}
              className="cropper__image"
              src={src}
              alt=""
              draggable={false}
              onLoad={onImageLoad}
              style={
                natural
                  ? {
                      width: natural.width * scale,
                      height: natural.height * scale,
                      left: offset.x,
                      top: offset.y
                    }
                  : { visibility: 'hidden' }
              }
            />
          )}
          <div className="cropper__mask" />
          <div className="cropper__ring" />
        </div>

        <label className="field" style={{ marginTop: 14 }}>
          <span className="field__label">Zoom</span>
          <input
            type="range"
            min={1}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(event) => applyZoom(Number(event.target.value))}
          />
        </label>

        {error && <div className="error-box" style={{ marginBottom: 10 }}>{error}</div>}

        <div className="btn-group">
          <button className="btn btn--primary" disabled={busy || !natural} onClick={() => void save()}>
            {busy ? 'Gemmer ...' : 'Brug billedet'}
          </button>
          <button className="btn" disabled={busy} onClick={onCancel}>
            Fortryd
          </button>
        </div>
      </div>
    </div>
  )
}
